<template>
  <div class="rounded-[20px] border border-gray-border bg-white shadow-panel-soft">
    <div class="flex flex-col gap-3 p-3 lg:hidden">
      <template v-if="loading">
        <div
          v-for="index in 3"
          :key="`finance-mobile-loading-${index}`"
          class="rounded-[18px] bg-white-gray p-4"
        >
          <div class="shimmer-row h-5 w-full rounded-[8px]"></div>
        </div>
      </template>

      <template v-else>
        <article
          v-for="row in tableRows"
          :key="row.id"
          class="rounded-[18px] p-4"
          :class="row.href ? 'border border-gray-border bg-white-gray' : 'bg-white'"
        >
          <div class="flex flex-col gap-3">
            <component
              :is="row.href ? (isInternalLink(row.href) ? 'NuxtLink' : 'a') : 'div'"
              :to="row.href && isInternalLink(row.href) ? internalHref(row.href) : undefined"
              :href="row.href && !isInternalLink(row.href) ? row.href : undefined"
              class="text-[16px] leading-[1.35]"
              :class="row.operationClass"
            >
              {{ row.operation }}
            </component>

            <div class="grid grid-cols-3 gap-3 text-[14px]">
              <div class="flex flex-col gap-1">
                <span class="uppercase tracking-[0.05em] text-extra-text-color">+</span>
                <span :class="row.valueClass">{{ row.plus || "—" }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="uppercase tracking-[0.05em] text-extra-text-color">-</span>
                <span :class="row.valueClass">{{ row.minus || "—" }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="uppercase tracking-[0.05em] text-extra-text-color">Баланс</span>
                <span :class="row.balanceClass">{{ row.balance || "—" }}</span>
              </div>
            </div>
          </div>
        </article>
      </template>
    </div>

    <div class="hidden overflow-x-auto lg:block">
      <table class="min-w-[760px] w-full border-collapse text-left">
      <thead
        class="border-b border-gray-border text-[16px] font-helvetica-700 font-bold text-gray-dark"
      >
        <tr>
          <th class="py-4 pl-5 pr-3 font-bold">Операция</th>
          <th class="py-4 px-3 font-bold text-right">+</th>
          <th class="py-4 px-3 font-bold text-right">-</th>
          <th class="py-4 px-3 font-bold text-right">Баланс</th>
        </tr>
      </thead>
      <tbody
        class="divide-y divide-gray-border text-[16px] font-helvetica-400 font-normal text-gray-dark"
      >
        <tr v-if="loading">
          <td colspan="4" class="py-4 px-3">
            <div class="shimmer-row h-5 w-full rounded-[8px]"></div>
          </td>
        </tr>
        <tr v-for="row in tableRows" :key="row.id" :class="row.rowClass">
          <td class="py-4 pl-5 pr-3 align-middle">
            <component
              v-if="row.href"
              :is="isInternalLink(row.href) ? 'NuxtLink' : 'a'"
              :to="isInternalLink(row.href) ? internalHref(row.href) : undefined"
              :href="isInternalLink(row.href) ? undefined : row.href"
              class="underline decoration-1 underline-offset-2 transition-colors hover:text-orange"
              :class="row.operationClass"
            >
              {{ row.operation }}
            </component>
            <span v-else :class="row.operationClass">{{ row.operation }}</span>
          </td>
          <td class="py-4 px-3 align-middle text-right">
            <span :class="row.valueClass">{{ row.plus }}</span>
          </td>
          <td class="py-4 px-3 align-middle text-right">
            <span :class="row.valueClass">{{ row.minus }}</span>
          </td>
          <td class="py-4 px-3 align-middle text-right">
            <span :class="row.balanceClass">{{ row.balance }}</span>
          </td>
        </tr>
      </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useReturnToLink } from "~/composables/useReturnToLink";

type FinanceEntry = {
  Договор?: string;
  Организация?: string;
  СуммаНачальныйОстаток?: string | number;
  СуммаКонечныйОстаток?: string | number;
  ВидДокумента?: string;
  СуммаРасход?: string | number;
  СуммаПриход?: string | number;
  НомерДокумента?: string;
  ДатаДокумента?: string;
  ИдДокумента?: string;
  СсылкаДокумента?: string;
  link?: string;
};

type FinancePayload = {
  ОбщийБалансНаНачалоПериода?: string | number;
  ОбщийБалансНаКонецПериода?: string | number;
  Данные?: FinanceEntry[];
};

type TableRow = {
  id: string;
  operation: string;
  plus: string;
  minus: string;
  balance: string;
  href?: string | null;
  operationClass: string;
  valueClass: string;
  balanceClass: string;
  rowClass: string;
};

const props = defineProps<{
  payload?: FinancePayload;
  loading?: boolean;
}>();

const loading = computed(() => props.loading ?? false);
const { withReturnTo } = useReturnToLink();

const payload = computed<FinancePayload>(() => props.payload ?? { Данные: [] });
const isInternalLink = (value?: string | null) =>
  typeof value === "string" && value.startsWith("/");
const internalHref = (value?: string | null) => withReturnTo(value);

const parseNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[\s\u00A0]/g, "").replace(",", ".");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatAmount = (value: unknown) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";
  const numeric = parseNumber(value);
  if (numeric === null) return text;
  const hasDecimal = /[.,]\d+/.test(text);
  const formatted = numeric.toLocaleString("ru-RU", {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: hasDecimal ? 2 : 0,
  });
  return formatted.replace(/\u00A0/g, " ");
};

const isGroupEntry = (entry: FinanceEntry) =>
  Boolean(entry.Договор || entry.Организация);

const buildOperationText = (entry: FinanceEntry) => {
  const parts = [
    entry.ВидДокумента?.trim(),
    entry.НомерДокумента ? `№ ${entry.НомерДокумента}` : null,
    entry.ДатаДокумента ? `от ${entry.ДатаДокумента}` : null,
  ].filter(Boolean);
  return parts.join(" ");
};

const buildGroupLabel = (entry: FinanceEntry, variant: "start" | "end") => {
  const contract = entry.Договор ? entry.Договор : "без договора";
  const org = entry.Организация ? `, ${entry.Организация}` : "";
  const suffix = variant === "start" ? "на начало периода" : "на конец периода";
  return `Договор: ${contract}${org} ${suffix}`;
};

const tableRows = computed<TableRow[]>(() => {
  const rows: TableRow[] = [];
  const data = payload.value;

  rows.push({
    id: "summary-start",
    operation: "Общий баланс на начало периода",
    plus: "",
    minus: "",
    balance: formatAmount(data.ОбщийБалансНаНачалоПериода),
    href: null,
    operationClass: "font-helvetica-700 text-gray-dark",
    valueClass: "",
    balanceClass: "font-helvetica-700 text-gray-dark",
    rowClass: "",
  });

  const entries = Array.isArray(data.Данные) ? data.Данные : [];
  let groupIndex = 0;
  let currentGroup: FinanceEntry | null = null;

  const pushGroupEndRow = (entry: FinanceEntry, index: number) => {
    const balance = formatAmount(entry.СуммаКонечныйОстаток);
    if (!balance) return;
    rows.push({
      id: `group-end-${index}`,
      operation: buildGroupLabel(entry, "end"),
      plus: "",
      minus: "",
      balance,
      href: null,
      operationClass: "text-[14px] text-extra-text-color",
      valueClass: "text-[14px] text-extra-text-color",
      balanceClass: "text-[14px] text-extra-text-color",
      rowClass: "",
    });
  };

  entries.forEach((entry, index) => {
    if (isGroupEntry(entry)) {
      if (currentGroup) {
        pushGroupEndRow(currentGroup, groupIndex);
      }
      groupIndex += 1;
      currentGroup = entry;
      rows.push({
        id: `group-start-${groupIndex}-${index}`,
        operation: buildGroupLabel(entry, "start"),
        plus: "",
        minus: "",
        balance: formatAmount(entry.СуммаНачальныйОстаток),
        href: null,
        operationClass: "text-[14px] text-extra-text-color",
        valueClass: "text-[14px] text-extra-text-color",
        balanceClass: "text-[14px] text-extra-text-color",
        rowClass: "",
      });
      return;
    }

    rows.push({
      id: entry.ИдДокумента || `doc-${index}`,
      operation: buildOperationText(entry),
      plus: formatAmount(entry.СуммаПриход),
      minus: formatAmount(entry.СуммаРасход),
      balance: "",
      href: entry.СсылкаДокумента ?? entry.link ?? null,
      operationClass: "",
      valueClass: "",
      balanceClass: "",
      rowClass: "transition-colors hover:bg-white-gray",
    });
  });

  if (currentGroup) {
    pushGroupEndRow(currentGroup, groupIndex);
  }

  rows.push({
    id: "summary-end",
    operation: "Общий баланс на конец периода",
    plus: "",
    minus: "",
    balance: formatAmount(data.ОбщийБалансНаКонецПериода),
    href: null,
    operationClass: "font-helvetica-700 text-gray-dark",
    valueClass: "",
    balanceClass: "font-helvetica-700 text-gray-dark",
    rowClass: "",
  });

  return rows;
});
</script>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer-row {
  background: var(--gradient-shimmer);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}
</style>
